import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';
import Button from '../../components/ui/Button';
import { useJobContext } from '../../contexts/CutlistJobContext';
import { getInventoryCode } from '../../calculations/inventoryCode';
import { generateItemNumbers } from '../../calculations/itemNumbering';
import { generateShopOrderPDF } from '../../pdf/shopOrderPDF';
import PDFPreviewModal from '../../components/PDFPreviewModal';
import {
  LayoutDashboard, FileText, Ruler, Factory, Link2, ArrowLeft, Printer, Check,
} from 'lucide-react';

const INK    = '#2B2620';
const WALNUT = '#8B4513';
const MOSS   = '#3B6B44';
const OCHRE  = '#A6752A';
const STONE  = '#8C8275';
const PAPER  = '#FDFBF8';

const FONT_DISPLAY = "'Fraunces', Georgia, serif";
const FONT_SANS    = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

function toFrac(val) {
  if (val === null || val === undefined || val === '') return '—';
  const n = parseFloat(val);
  if (isNaN(n)) return '—';
  const whole = Math.floor(n);
  const d     = n - whole;
  const fracs = [
    [0,''], [0.0625,'1/16'], [0.125,'1/8'], [0.1875,'3/16'],
    [0.25,'1/4'], [0.3125,'5/16'], [0.375,'3/8'], [0.4375,'7/16'],
    [0.5,'1/2'], [0.5625,'9/16'], [0.625,'5/8'], [0.6875,'11/16'],
    [0.75,'3/4'], [0.8125,'13/16'], [0.875,'7/8'], [0.9375,'15/16'],
  ];
  let closest = fracs[0], minDiff = Math.abs(d);
  for (const f of fracs) {
    const diff = Math.abs(d - f[0]);
    if (diff < minDiff) { minDiff = diff; closest = f; }
  }
  if (closest[1] === '') return whole === 0 ? '0' : String(whole);
  return whole === 0 ? closest[1] : `${whole} ${closest[1]}`;
}

function getScreenLabel(w) {
  if (w.flexScreen) {
    const size = w.screenSize || '';
    return size.toLowerCase().includes('half') ? 'Half' : 'Full';
  }
  if (!w.screenType || w.screenType === 'None') return '';
  const size = w.screenSize || '';
  return size.toLowerCase().includes('half') ? 'Half' : 'Full';
}

function getFlangesLabel(w) {
  const fin = w.finType || '';
  if (!fin) return '';
  return fin.toLowerCase().includes('without') ? '' : 'Yes';
}

export default function ShopOrder() {
  const navigate = useNavigate();
  const { id: urlJobId } = useParams();

  const { job, calculatedWindows, linkedJobId } = useJobContext();
  const [previewBlob, setPreviewBlob] = useState(null);

  const enriched = calculatedWindows.map(w => ({
    ...w,
    ...getInventoryCode(w),
    screenLabel:  getScreenLabel(w),
    flangesLabel: getFlangesLabel(w),
    hasGrids:     !!(w.glass?.[0]?.grids && (w.glass[0].gridQtyV > 0 || w.glass[0].gridQtyH > 0)),
  }));

  const rows     = enriched.length > 0 ? generateItemNumbers(job.woNumber || 'DRAFT', enriched) : [];
  const totalQty = rows.reduce((a, r) => a + parseFloat(r.qty || 1), 0);

  // ── KEY FIX: navigate to /cut-list with autoLinkJob state ─────────────────
  // Plain navigate('/cut-list') causes a page reload that wipes React context.
  // Passing the full job payload as autoLinkJob state means CutList.jsx picks it
  // up in its useEffect, calls loadFromJob(), restores everything, and since
  // calculatedWindows is included, auto-jumps to preview view — all links work.
  function goToCutList() {
    navigate(`/cutlist/${urlJobId}/cutlist`, {
      state: {
        autoLinkJob: {
          ...job,
          id:                linkedJobId,
          calculatedWindows: calculatedWindows,
        },
      },
    });
  }

  const STEPS = [
    { id: 'dashboard', step: 1, Icon: LayoutDashboard, label: 'Job Dashboard', onClick: () => navigate('/cutlist') },
    { id: 'jobentry',  step: 2, Icon: FileText,        label: 'Job Entry',     onClick: goToCutList },
    { id: 'preview',   step: 3, Icon: Ruler,           label: 'Cut List',      onClick: goToCutList },
    { id: 'shoporder', step: 4, Icon: Factory,         label: 'Shop Order',    active: true },
  ];

  const breadcrumb = 'Dashboard › Cutlist Dashboard › ' + (job.customerName || 'Shop Order');

  return (
    <AppLayout
      title="Shop Order"
      breadcrumb={breadcrumb}
      topBarActions={
        rows.length > 0 ? (
          <>
            <Button size="small" onClick={() => { const blob = generateShopOrderPDF(job, calculatedWindows, true); setPreviewBlob(blob); }}>
              <Printer size={14} strokeWidth={2} /> Preview / Download PDF
            </Button>
            <Button size="small" onClick={goToCutList}><ArrowLeft size={14} strokeWidth={2} /> Cut List</Button>
          </>
        ) : null
      }
    >
      <div style={{ maxWidth: '1200px', width: '100%', margin: '0 auto', boxSizing: 'border-box', fontFamily: FONT_SANS }}>

        {/* Top row: back link + linked job chip */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '18px', flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate('/cutlist')}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', color: WALNUT, fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: FONT_SANS, padding: 0 }}
          >
            <ArrowLeft size={14} strokeWidth={2} /> Back to Cutlist Dashboard
          </button>
          <div style={{ flex: 1 }} />
          {linkedJobId && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 12px', borderRadius: '20px', background: 'rgba(59,107,68,0.08)', border: '1px solid rgba(59,107,68,0.2)', fontSize: '11px', fontWeight: '600', color: MOSS }}>
              <Link2 size={12} strokeWidth={2} /> {job.customerName || 'Linked job'}{job.woNumber ? ` — WO# ${job.woNumber}` : ''}
            </div>
          )}
        </div>

        {/* Step progress bar */}
        <div style={{ background: PAPER, borderRadius: '14px', border: '1px solid rgba(139,69,19,0.1)', boxShadow: '0 2px 12px rgba(139,69,19,0.06)', padding: '14px 20px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '4px', overflowX: 'auto' }}>
          {STEPS.map((s, i) => {
            const isActive = !!s.active;
            const isDone   = s.step < 4;
            const color = isActive ? WALNUT : isDone ? MOSS : STONE;
            return (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', flex: i < STEPS.length - 1 ? 1 : 'none', minWidth: 'fit-content' }}>
                <button
                  onClick={s.onClick}
                  disabled={!s.onClick}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '7px 12px', borderRadius: '20px', border: 'none',
                    background: isActive ? WALNUT : 'transparent',
                    color: isActive ? 'white' : color,
                    fontSize: '12.5px', fontWeight: isActive ? '700' : '600',
                    cursor: s.onClick ? 'pointer' : 'default',
                    fontFamily: FONT_SANS, whiteSpace: 'nowrap',
                  }}
                >
                  {isDone ? <Check size={14} strokeWidth={2.5} /> : <s.Icon size={14} strokeWidth={2} />}
                  {s.label}
                </button>
                {i < STEPS.length - 1 && (
                  <div style={{ flex: 1, height: '1px', minWidth: '16px', background: isDone ? MOSS : 'rgba(139,69,19,0.12)' }} />
                )}
              </div>
            );
          })}
        </div>

        {/* Content */}
        {rows.length === 0 ? (
          <div style={{ background: PAPER, borderRadius: '16px', border: '2px dashed rgba(139,69,19,0.15)', padding: '80px 32px', textAlign: 'center' }}>
            <Factory size={44} strokeWidth={1.5} color={STONE} style={{ marginBottom: '16px', opacity: 0.6 }} />
            <p style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '600', color: STONE }}>No job data available</p>
            <p style={{ margin: '0 0 24px', fontSize: '13px', color: STONE }}>Open a job from the Dashboard and calculate a cut list first</p>
            <Button onClick={() => navigate('/cutlist')}>
              <LayoutDashboard size={14} strokeWidth={2} /> Go to Dashboard
            </Button>
          </div>
        ) : (
          <div>
            {/* Job info card */}
            <div style={{ background: PAPER, borderRadius: '16px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 12px rgba(139,69,19,0.06)', border: '1px solid rgba(139,69,19,0.1)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: WALNUT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Factory size={18} strokeWidth={2} color="white" />
                </div>
                <div>
                  <h2 style={{ margin: 0, fontFamily: FONT_DISPLAY, fontSize: '17px', fontWeight: 600, color: INK }}>Shop Order Preview</h2>
                  <p style={{ margin: 0, fontSize: '11px', color: STONE }}>Auto-generated from Cut List data</p>
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px', background: WALNUT, color: 'white', padding: '6px 16px', borderRadius: '999px', fontSize: '13px', fontWeight: '700' }}>
                  {rows.length} window{rows.length !== 1 ? 's' : ''}
                  <span style={{ width: '3px', height: '3px', borderRadius: '50%', background: 'rgba(255,255,255,0.6)' }} />
                  {totalQty.toFixed(0)} total qty
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
                {[
                  ['Customer', job.customerName || '—'],
                  ['Job Name', job.jobName      || '—'],
                  ['WO #',     job.woNumber     || '—'],
                  ['Date',     job.orderDate    || '—'],
                ].map(([label, value]) => (
                  <div key={label}>
                    <div style={{ fontSize: '10px', fontWeight: '700', color: WALNUT, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>{label}</div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: INK }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Table */}
            <div style={{ background: PAPER, borderRadius: '16px', overflow: 'hidden', boxShadow: '0 2px 12px rgba(139,69,19,0.06)', border: '1px solid rgba(139,69,19,0.1)', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: 'rgba(139,69,19,0.04)', borderBottom: '2px solid rgba(139,69,19,0.15)' }}>
                    {['Qty', 'Width × Height', 'Series', 'Code', 'Item #', 'Screen', 'Flanges', 'Notes'].map(h => (
                      <th key={h} style={{
                        padding: '12px 14px',
                        textAlign: ['Qty', 'Item #', 'Screen', 'Flanges', 'Code'].includes(h) ? 'center' : 'left',
                        fontSize: '11px', fontWeight: '700', color: WALNUT,
                        textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap',
                      }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr
                      key={i}
                      style={{ borderBottom: '1px solid rgba(139,69,19,0.06)', background: i % 2 === 0 ? PAPER : 'rgba(139,69,19,0.015)', transition: 'background 0.15s' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(139,69,19,0.04)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = i % 2 === 0 ? PAPER : 'rgba(139,69,19,0.015)'; }}
                    >
                      <td style={{ padding: '12px 14px', textAlign: 'center', fontWeight: '600', color: INK, whiteSpace: 'nowrap' }}>
                        {parseFloat(row.qty || 1).toFixed(2)}
                      </td>
                      <td style={{ padding: '12px 14px', textAlign: 'center', fontFamily: "'IBM Plex Mono', monospace", fontWeight: '600', color: INK, whiteSpace: 'nowrap' }}>
                        {toFrac(row.overallW)} × {toFrac(row.height)}
                      </td>
                      <td style={{ padding: '12px 14px', color: INK }}>{row.series}</td>
                      <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                        <span style={{ background: 'rgba(139,69,19,0.06)', color: WALNUT, padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', fontFamily: "'IBM Plex Mono', monospace" }}>
                          {row.code}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                        <span style={{ background: 'rgba(139,69,19,0.08)', color: WALNUT, padding: '3px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: '700' }}>
                          {row.itemNumber}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                        {row.screenLabel ? (
                          <span style={{ background: 'rgba(59,107,68,0.08)', color: MOSS, padding: '3px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: '600' }}>
                            {row.screenLabel}
                          </span>
                        ) : (
                          <span style={{ color: STONE, fontSize: '12px' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                        {row.flangesLabel === 'Yes' ? (
                          <span style={{ background: 'rgba(139,69,19,0.08)', color: WALNUT, padding: '3px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: '600' }}>
                            Yes
                          </span>
                        ) : (
                          <span style={{ color: STONE, fontSize: '12px' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: '12px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', alignItems: 'center' }}>
                          {row.options && (
                            <span style={{ color: WALNUT, background: 'rgba(139,69,19,0.07)', borderLeft: `2px solid ${WALNUT}`, paddingLeft: '6px', paddingRight: '8px', paddingTop: '2px', paddingBottom: '2px', borderRadius: '0 5px 5px 0', fontSize: '11px', fontWeight: '500' }}>
                              {row.options}
                            </span>
                          )}
                          {row.hasGrids && (
                            <span style={{ background: `${OCHRE}12`, color: OCHRE, padding: '2px 7px', borderRadius: '5px', fontSize: '11px', fontWeight: '500' }}>
                              Grids
                            </span>
                          )}
                          {!row.options && !row.hasGrids && (
                            <span style={{ color: STONE }}>—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: 'rgba(139,69,19,0.04)', borderTop: '2px solid rgba(139,69,19,0.15)' }}>
                    <td style={{ padding: '12px 14px', textAlign: 'center', fontWeight: '700', color: WALNUT, fontSize: '14px' }}>
                      {totalQty.toFixed(2)}
                    </td>
                    <td colSpan={7} style={{ padding: '12px 14px', color: STONE, fontSize: '12px', fontWeight: '600' }}>
                      Total  •  {rows.length} line item{rows.length !== 1 ? 's' : ''}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </div>

      {previewBlob && (
        <PDFPreviewModal
          pdfBlob={previewBlob}
          filename={`${(job.customerName || 'Customer').replace(/\s+/g, '_')}_WO${job.woNumber || 'DRAFT'}_ShopOrder.pdf`}
          onClose={() => setPreviewBlob(null)}
          landscape
        />
      )}
    </AppLayout>
  );
}
